/**
 * B2B Outreach Admin Hub.
 *
 * Single-page admin UI for the B2B sales outreach pipeline. Tabs:
 *   - Dashboard    KPIs, funnel, recent activity
 *   - Leads        Companies + jobs view (seed + filter)
 *   - Campaigns    CRUD campaigns + monitor stats
 *   - Review       Pending drafts review queue (approve/reject/edit/regenerate)
 *   - Inbox        Inbound replies grouped by intent
 *   - Blocklist    Email/domain suppression list
 *
 * Routed at /admin/b2b behind <AdminGuard>.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LayoutDashboard, Building2, Megaphone, ClipboardCheck, Inbox,
  ShieldOff, Loader2, RefreshCw,
} from 'lucide-react';

import { useAuth } from '../../hooks/useAuth';
import {
  type HeadlineKPIs, type FunnelStep, type TimeseriesPoint, type RecentEvent,
  fetchSummary, fetchFunnel, fetchTimeseries, fetchRecent,
} from '../../api/b2b';

import B2BTabLeads from '../../components/b2b/B2BTabLeads';
import B2BTabCampaigns from '../../components/b2b/B2BTabCampaigns';
import B2BTabReview from '../../components/b2b/B2BTabReview';
import B2BTabInbox from '../../components/b2b/B2BTabInbox';
import B2BTabBlocklist from '../../components/b2b/B2BTabBlocklist';
import { Centered, ErrorBox } from '../../components/b2b/_ui';

type TabId = 'dashboard' | 'leads' | 'campaigns' | 'review' | 'inbox' | 'blocklist';

const TABS: { id: TabId; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { id: 'leads',     label: 'Leads',     Icon: Building2 },
  { id: 'campaigns', label: 'Campaigns', Icon: Megaphone },
  { id: 'review',    label: 'Review',    Icon: ClipboardCheck },
  { id: 'inbox',     label: 'Inbox',     Icon: Inbox },
  { id: 'blocklist', label: 'Blocklist', Icon: ShieldOff },
];

const B2BHub: React.FC = () => {
  const { apiCall } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <header className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900">B2B Outreach</h1>
          <p className="text-sm text-slate-500">
            Cold-Outreach Pipeline · Companies → Jobs → Contacts → Drafts → Sends → Replies
          </p>
        </header>

        <nav className="flex gap-2 mb-6 overflow-x-auto pb-1 border-b border-slate-200">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === id
                  ? 'bg-white text-pink-600 border-b-2 border-pink-600 -mb-px'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>

        <div className="space-y-6">
          {activeTab === 'dashboard' && <DashboardPanel apiCall={apiCall} />}
          {activeTab === 'leads'     && <B2BTabLeads apiCall={apiCall} />}
          {activeTab === 'campaigns' && <B2BTabCampaigns apiCall={apiCall} />}
          {activeTab === 'review'    && <B2BTabReview apiCall={apiCall} />}
          {activeTab === 'inbox'     && <B2BTabInbox apiCall={apiCall} />}
          {activeTab === 'blocklist' && <B2BTabBlocklist apiCall={apiCall} />}
        </div>
      </div>
    </div>
  );
};

export default B2BHub;

// ============================================================
// Dashboard panel (kept inline since it's compact)
// ============================================================

interface DashboardProps { apiCall: ReturnType<typeof useAuth>['apiCall']; }

const DashboardPanel: React.FC<DashboardProps> = ({ apiCall }) => {
  const [kpis, setKpis] = useState<HeadlineKPIs | null>(null);
  const [funnel, setFunnel] = useState<FunnelStep[]>([]);
  const [series, setSeries] = useState<TimeseriesPoint[]>([]);
  const [recent, setRecent] = useState<RecentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, f, ts, r] = await Promise.all([
        fetchSummary(apiCall),
        fetchFunnel(apiCall),
        fetchTimeseries(apiCall, 30),
        fetchRecent(apiCall, 20),
      ]);
      setKpis(s);
      setFunnel(f.steps);
      setSeries(ts);
      setRecent(r);
    } catch (e: any) {
      setError(e?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <Centered><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></Centered>;
  }
  if (error) {
    return <ErrorBox message={error} onRetry={load} />;
  }
  if (!kpis) return null;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Companies" value={kpis.companies_total} sub={`${kpis.companies_with_ats} with ATS`} />
        <KpiCard
          label="Open Jobs"
          value={kpis.open_jobs_total}
          sub={
            kpis.open_jobs_unclassified > 0
              ? `${kpis.open_jobs_target_tech} tech · ${kpis.open_jobs_unclassified} unclassified`
              : `${kpis.open_jobs_target_tech} tech`
          }
          accent
        />
        <KpiCard label="Contacts" value={kpis.contacts_total} sub={`${kpis.contacts_verified} verified`} />
        <KpiCard label="Sends (30d)" value={kpis.sends_30d} sub={`${kpis.sends_24h} today`} />
        <KpiCard label="Reply rate" value={`${(kpis.reply_rate_30d * 100).toFixed(1)}%`} sub={`${kpis.replies_30d} replies / 30d`} />
        <KpiCard label="Positive rate" value={`${(kpis.positive_rate_30d * 100).toFixed(1)}%`} sub={`${kpis.positive_replies_30d} positive / 30d`} accent />
        <KpiCard label="Unsubscribe rate" value={`${(kpis.unsubscribe_rate_30d * 100).toFixed(2)}%`} sub="30d" />
        <KpiCard label="Active Pipeline" value={kpis.companies_with_open_jobs} sub="companies hiring" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-800">Funnel</h2>
            <button onClick={load} className="text-slate-400 hover:text-slate-600">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <FunnelChart steps={funnel} />
          <div className="mt-6">
            <h3 className="font-medium text-slate-700 text-sm mb-2">Last 30 days</h3>
            <TimeseriesChart points={series} />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h2 className="font-semibold text-slate-800 mb-3">Recent activity</h2>
          <ul className="space-y-2 max-h-[28rem] overflow-y-auto">
            {recent.length === 0 && <li className="text-sm text-slate-400">No activity yet</li>}
            {recent.map((e, idx) => (
              <li key={idx} className="text-sm flex flex-col">
                <span className={`inline-flex w-fit mb-0.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                  e.kind === 'reply' ? 'bg-emerald-100 text-emerald-700' :
                  e.kind === 'send'  ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-600'
                }`}>{e.kind}</span>
                <span className="text-slate-700 truncate" title={e.label}>{e.label}</span>
                <span className="text-[11px] text-slate-400">
                  {e.company_domain || ''}{e.company_domain && e.contact_email ? ' · ' : ''}{e.contact_email || ''}
                </span>
                <span className="text-[10px] text-slate-400">{new Date(e.when).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

// ============================================================
// Small UI helpers (Centered/ErrorBox live in components/b2b/_ui.tsx
// to keep the import graph acyclic — tab components import from there.)
// ============================================================

const KpiCard: React.FC<{ label: string; value: number | string; sub?: string; accent?: boolean }> =
  ({ label, value, sub, accent }) => (
    <div className={`rounded-lg p-4 border ${accent ? 'border-pink-200 bg-pink-50' : 'border-slate-200 bg-white'}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-0.5 ${accent ? 'text-pink-700' : 'text-slate-900'}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );

const FunnelChart: React.FC<{ steps: FunnelStep[] }> = ({ steps }) => {
  const max = useMemo(() => Math.max(1, ...steps.map((s) => s.count)), [steps]);
  return (
    <div className="space-y-1.5">
      {steps.map((s, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <div className="w-44 text-xs text-slate-600 truncate">{s.label}</div>
          <div className="flex-1 bg-slate-100 rounded h-5 relative overflow-hidden">
            <div
              className="bg-gradient-to-r from-pink-500 to-pink-400 h-full rounded transition-all"
              style={{ width: `${(s.count / max) * 100}%` }}
            />
          </div>
          <div className="w-12 text-right text-xs font-medium text-slate-700">{s.count}</div>
        </div>
      ))}
    </div>
  );
};

const TimeseriesChart: React.FC<{ points: TimeseriesPoint[] }> = ({ points }) => {
  if (points.length === 0) return <div className="text-sm text-slate-400">No data</div>;

  const maxSends = Math.max(1, ...points.map((p) => p.sends));
  const maxReplies = Math.max(1, ...points.map((p) => p.replies));

  return (
    <div className="space-y-2">
      <div>
        <div className="text-[11px] text-slate-500 mb-1">Sends</div>
        <div className="flex items-end gap-0.5 h-12">
          {points.map((p, idx) => (
            <div
              key={idx}
              className="flex-1 bg-blue-200 rounded-sm"
              style={{ height: `${Math.max(2, (p.sends / maxSends) * 100)}%` }}
              title={`${p.day}: ${p.sends} sends`}
            />
          ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] text-slate-500 mb-1">Replies (positive in dark)</div>
        <div className="flex items-end gap-0.5 h-10">
          {points.map((p, idx) => (
            <div
              key={idx}
              className="flex-1 relative rounded-sm bg-emerald-100"
              style={{ height: `${Math.max(2, (p.replies / maxReplies) * 100)}%` }}
              title={`${p.day}: ${p.replies} replies (${p.positive} positive)`}
            >
              {p.positive > 0 && (
                <div
                  className="absolute inset-x-0 bottom-0 bg-emerald-500 rounded-sm"
                  style={{ height: `${(p.positive / Math.max(1, p.replies)) * 100}%` }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
